import {
  Component,
  ChangeDetectionStrategy,
  computed,
  inject,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { MenuModule } from 'primeng/menu';
import { Menu } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, take } from 'rxjs/operators';
import { AuthService } from '@auth0/auth0-angular';
import { NotificationsService } from '../services/notifications.service';
import { OrgContextService } from '../services/org-context.service';
import { DELIBERATE_LOGOUT_KEY, DeliberateLogoutFlag } from '../auth/session-constants';

@Component({
  selector: 'lib-app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, MenuModule],
  templateUrl: './header.html',
  styleUrl: './header.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  private readonly profileMenu = viewChild<Menu>('profileMenu');

  private auth = inject(AuthService);
  private notifications = inject(NotificationsService);
  private orgContextService = inject(OrgContextService);

  constructor() {
    // Open the real-time notification channel once the user is authenticated.
    this.auth.isAuthenticated$
      .pipe(filter(Boolean), take(1), takeUntilDestroyed())
      .subscribe(() => this.notifications.start());
  }

  private readonly user = toSignal(this.auth.user$);
  private readonly orgContext = toSignal(this.orgContextService.resolve());

  readonly avatarPicture = computed(() => this.user()?.picture);

  readonly avatarInitial = computed(() => {
    const u = this.user();
    const source = u?.name || u?.nickname || u?.email;
    return source ? source.charAt(0).toUpperCase() : 'J';
  });

  readonly orgLogoUrl = computed(() => this.orgContext()?.logoUrl ?? null);

  readonly orgDisplayName = computed(() =>
    this.orgContext()?.displayName ?? 'Ka-boom'
  );

  readonly orgLogoInitial = computed(() =>
    (this.orgContext()?.displayName ?? 'K').charAt(0).toUpperCase()
  );

  readonly profileMenuItems: MenuItem[] = [
    {
      label: 'Profile',
      icon: 'pi pi-user',
      routerLink: '/profile',
    },
    {
      label: 'Settings',
      icon: 'pi pi-cog',
      routerLink: '/settings',
    },
    {
      label: 'Manage team',
      icon: 'pi pi-users',
      routerLink: '/team',
    },
    {
      label: 'Switch tenant',
      icon: 'pi pi-sync',
      routerLink: '/switch-tenant',
    },
    {
      label: 'Help',
      icon: 'pi pi-question-circle',
      routerLink: '/help',
    },
    { separator: true },
    {
      label: 'Log out',
      icon: 'pi pi-sign-out',
      command: () => this.logout(),
    },
  ];

  toggleProfileMenu(event: MouseEvent): void {
    this.profileMenu()?.toggle(event);
  }

  private logout(): void {
    // Flag deliberate logout intent so the app-level SessionService's
    // isAuthenticated$ watcher doesn't misread the incoming false as
    // an expiry and show a "session expired" dialog to a user who just
    // chose to sign out. sessionStorage keeps the flag per-tab and
    // clears itself when the tab closes. The key lives in
    // libs/shared/auth/session-constants so both sides of the boundary
    // (this header and the app-level SessionService) resolve to the
    // same identifier — no string duplication.
    try {
      // Store a timestamp so SessionService can enforce a TTL — if the
      // logout redirect is blocked (CSP, extension, network failure) and
      // the SPA stays alive, an ancient flag would otherwise silently
      // swallow the NEXT real expiry in this tab. Date.now(), not a
      // module-level constant, so the age check is genuinely dynamic.
      const flag: DeliberateLogoutFlag = { ts: Date.now() };
      sessionStorage.setItem(DELIBERATE_LOGOUT_KEY, JSON.stringify(flag));
    } catch {
      // sessionStorage unavailable — user will briefly see the expired
      // dialog before the logout redirect unloads the SPA. Acceptable.
    }
    this.auth.logout({
      logoutParams: { returnTo: window.location.origin },
    });
  }
}
